import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { getSlashCommandComponent } from 'features/richText/components/slashCommandCards'
import {
  $getNodeByKey,
  DecoratorNode,
  DOMConversionMap,
  DOMConversionOutput,
  LexicalNode,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from 'lexical'
import React from 'react'
import { GeckoEntry, RolodexEntryType } from 'types'

interface SlashCommandWrapperProps {
  entryType: RolodexEntryType
  entryId: string
  entryName: string
  metadata?: Record<string, string | null | undefined>
}

const SlashCommandWrapper: React.FC<
  SlashCommandWrapperProps & { nodeKey?: NodeKey }
> = ({ entryType, entryId, entryName, metadata, nodeKey }) => {
  const [editor] = useLexicalComposerContext()
  const isEditMode = editor.isEditable()
  const Component = getSlashCommandComponent(entryType)

  const onMetadataChange = React.useCallback(
    (newMetadata: Record<string, string | null | undefined>) => {
      if (!nodeKey) return

      editor.update(() => {
        const node = $getNodeByKey(nodeKey)
        if (node && $isSlashCommandNode(node)) {
          node.setMetadata(newMetadata)
        }
      })
    },
    [editor, nodeKey]
  )

  return (
    <Component
      entryType={entryType}
      entryID={entryId}
      entryName={entryName}
      metadata={metadata}
      isEditMode={isEditMode}
      onMetadataChange={onMetadataChange}
    />
  )
}

// Helper function to convert HTML element to SlashCommandNode
function convertSlashCommandElement(domNode: HTMLElement): DOMConversionOutput {
  const entryType = domNode.getAttribute('data-entry-type') as RolodexEntryType
  const entryId = domNode.getAttribute('data-entry-id') || ''
  const entryName = domNode.getAttribute('data-entry-name') || ''
  const metadataStr = domNode.getAttribute('data-metadata')

  let metadata: Record<string, string | null | undefined> | undefined
  if (metadataStr) {
    try {
      metadata = JSON.parse(metadataStr)
    } catch {
      metadata = undefined
    }
  }

  if (entryType && entryId) {
    const node = new SlashCommandNode({
      entryType,
      entryId,
      entryName,
      metadata,
    })
    return { node }
  }

  return { node: null }
}

export type SerializedSlashCommandNode = Spread<
  {
    entryType: RolodexEntryType
    entryId: string
    entryName: string
    metadata?: Record<string, string | null | undefined>
  },
  SerializedLexicalNode
>

export interface SlashCommandPayload {
  entryType: RolodexEntryType
  entryId: string
  entryName: string
  metadata?: Record<string, string | null | undefined>
}

export class SlashCommandNode extends DecoratorNode<React.JSX.Element> {
  __entryType: RolodexEntryType
  __entryId: string
  __entryName: string
  __metadata?: Record<string, string | null | undefined>

  static getType(): string {
    return 'slash-command'
  }

  static clone(node: SlashCommandNode): SlashCommandNode {
    return new SlashCommandNode(
      {
        entryType: node.__entryType,
        entryId: node.__entryId,
        entryName: node.__entryName,
        metadata: node.__metadata,
      },
      node.__key
    )
  }

  constructor(payload: SlashCommandPayload, key?: NodeKey) {
    super(key)
    this.__entryType = payload.entryType
    this.__entryId = payload.entryId
    this.__entryName = payload.entryName
    this.__metadata = payload.metadata
  }

  exportJSON(): SerializedSlashCommandNode {
    return {
      entryType: this.__entryType,
      entryId: this.__entryId,
      entryName: this.__entryName,
      metadata: this.__metadata,
      type: 'slash-command',
      version: 1,
    }
  }

  static importJSON(
    serializedNode: SerializedSlashCommandNode
  ): SlashCommandNode {
    return new SlashCommandNode({
      entryType: serializedNode.entryType,
      entryId: serializedNode.entryId,
      entryName: serializedNode.entryName,
      metadata: serializedNode.metadata,
    })
  }

  static importDOM(): DOMConversionMap | null {
    return {
      span: (domNode: HTMLElement) => {
        if (!domNode.hasAttribute('data-lexical-slash-command')) {
          return null
        }
        return {
          conversion: convertSlashCommandElement,
          priority: 1,
        }
      },
    }
  }

  createDOM(): HTMLElement {
    const element = document.createElement('span')
    element.setAttribute('data-lexical-slash-command', 'true')
    element.setAttribute('data-entry-type', this.__entryType)
    element.setAttribute('data-entry-id', this.__entryId)
    element.setAttribute('data-entry-name', this.__entryName)
    if (this.__metadata) {
      element.setAttribute('data-metadata', JSON.stringify(this.__metadata))
    }
    return element
  }

  updateDOM(): false {
    return false
  }

  setMetadata(metadata: Record<string, string | null | undefined>): void {
    const writable = this.getWritable()
    writable.__metadata = metadata
  }

  getMetadata(): Record<string, string | null | undefined> | undefined {
    return this.getLatest().__metadata
  }

  decorate(): React.JSX.Element {
    return (
      <SlashCommandWrapper
        entryType={this.__entryType}
        entryId={this.__entryId}
        entryName={this.__entryName}
        metadata={this.__metadata}
        nodeKey={this.__key}
      />
    )
  }

  getTextContent(): string {
    return `/${this.__entryName}`
  }

  isInline(): boolean {
    return true
  }

  isKeyboardSelectable(): boolean {
    return true
  }
}

// Helper function to create slash command nodes from entry data
export const createSlashCommandNode = (entry: GeckoEntry): SlashCommandNode => {
  return new SlashCommandNode({
    entryType: entry.type,
    entryId: entry.id,
    entryName:
      (entry.data.name as string | undefined) || `${entry.type} ${entry.id}`,
    metadata: {
      siteId: entry.tags?.site,
      orgId: entry.tags?.organization,
    },
  })
}

// Function to check if a node is a slash command node
export const $isSlashCommandNode = (
  node: LexicalNode | null | undefined
): node is SlashCommandNode => {
  return node instanceof SlashCommandNode
}
